import { CRAWL_MATRIX_DEPTHS } from "./config.js";
import { z } from "zod";

//#region src/lib/crawl/data.ts
const CountSchema = z.number().int().min(0);
const IsoTimeSchema = z.string().datetime();
const CentsSchema = z.number().int().min(0);
const IdProductSchema = z.number().int().positive().meta({ examples: [882902] });
const IdGameSchema = z.number().int().positive().meta({ examples: [1] });
/** The six conditions a stored price row holds, best first; Mint counts as Near Mint. */
const CRAWL_DATA_CONDITIONS = [
	"NM",
	"EX",
	"GD",
	"LP",
	"PL",
	"PO"
];
const ConditionSchema = z.enum(CRAWL_DATA_CONDITIONS);
/** Cardmarket language id. */
const LanguageIdSchema = z.number().int().positive().meta({ examples: [1, 3] });
/** Count of products per condition. */
const ConditionCountsSchema = z.object({
	NM: CountSchema,
	EX: CountSchema,
	GD: CountSchema,
	LP: CountSchema,
	PL: CountSchema,
	PO: CountSchema
});
/** Cheapest price per condition in cents, null when none is stored. */
const ConditionPricesSchema = z.object({
	NM: CentsSchema.nullable(),
	EX: CentsSchema.nullable(),
	GD: CentsSchema.nullable(),
	LP: CentsSchema.nullable(),
	PL: CentsSchema.nullable(),
	PO: CentsSchema.nullable()
});
const CrawlDataCoverageInputSchema = z.object({
	gameId: IdGameSchema,
	ruleKey: z.string().min(1).optional().meta({ examples: ["pricy"] })
});
const CrawlDataCoverageLanguageSchema = z.object({
	languageId: LanguageIdSchema,
	label: z.string().meta({ examples: ["English"] }),
	conditions: ConditionCountsSchema
});
const CrawlDataCoverageSchema = z.object({
	gameId: IdGameSchema,
	ruleKeys: z.array(z.string()),
	products: CountSchema,
	productsCrawled: CountSchema,
	productsWithPrice: CountSchema,
	languages: z.array(CrawlDataCoverageLanguageSchema)
});
/**
* What we hold for a product in one language and condition:
* `price_found` a stored price; `no_offer_found` crawled, and the crawl found
* no offer there; `not_read` crawled, but the offers read did not reach that
* condition; `never_crawled` no successful crawl yet.
*/
const CRAWL_DATA_PRODUCT_STATUSES = [
	"price_found",
	"no_offer_found",
	"not_read",
	"never_crawled"
];
const CrawlListDataProductsInputSchema = z.object({
	gameId: IdGameSchema,
	ruleKey: z.string().min(1).optional(),
	languageId: LanguageIdSchema,
	condition: ConditionSchema,
	withPrice: z.boolean(),
	search: z.string().trim().max(200).optional(),
	offset: z.number().int().min(0).default(0),
	limit: z.number().int().min(1).max(200).default(50),
	sortBy: z.enum([
		"name",
		"price",
		"lastCrawledAt",
		"lastSavedAt"
	]).default("name"),
	sortDirection: z.enum(["asc", "desc"]).default("asc")
});
/** What we hold for one finish of a product in the cell's language and condition. */
const CrawlDataProductFinishSchema = z.object({
	isFoil: z.boolean().nullable(),
	isReverseHolo: z.boolean().nullable(),
	isFirstEd: z.boolean().nullable(),
	status: z.enum(CRAWL_DATA_PRODUCT_STATUSES),
	priceCents: CentsSchema.nullable(),
	lastSavedAt: IsoTimeSchema.nullable()
});
const CrawlDataProductRowSchema = z.object({
	idProduct: IdProductSchema,
	name: z.string().meta({ examples: ["Mana Vault"] }),
	expansion: z.string().nullable().meta({ examples: ["Fourth-Edition"] }),
	ruleKey: z.string().nullable(),
	status: z.enum(CRAWL_DATA_PRODUCT_STATUSES),
	finishes: z.array(CrawlDataProductFinishSchema),
	lastCrawledAt: IsoTimeSchema.nullable(),
	lastOutcome: z.string().nullable().meta({ examples: ["complete", "blocked"] }),
	lastSavedAt: IsoTimeSchema.nullable()
});
const CrawlDataProductsPageSchema = z.object({
	rows: z.array(CrawlDataProductRowSchema),
	total: CountSchema
});
const CrawlSearchDataProductsInputSchema = z.object({
	query: z.string().trim().min(1).max(200).meta({ examples: ["Mana Vault", "882902"] }),
	gameId: IdGameSchema.optional(),
	limit: z.number().int().min(1).max(50).default(20)
});
const CrawlDataProductMatchSchema = z.object({
	idProduct: IdProductSchema,
	gameId: IdGameSchema,
	gameName: z.string(),
	name: z.string(),
	expansion: z.string().nullable()
});
const CrawlDataProductMatchesSchema = z.object({ rows: z.array(CrawlDataProductMatchSchema) });
const CrawlDataProductInputSchema = z.object({ idProduct: IdProductSchema });
/** The offer a stored price was read from. */
const CrawlDataProvingOfferSchema = z.object({
	articleId: z.string(),
	sellerName: z.string(),
	sellerType: z.string(),
	sellerCountry: z.string().nullable(),
	quantity: z.number().int().nullable()
});
/**
* One condition of a stored row: `proven` a price was found, `empty` no
* offer, `unknown` the offers read did not reach this condition.
*/
const CrawlDataConditionDetailSchema = z.object({
	status: z.enum([
		"proven",
		"empty",
		"unknown"
	]),
	pricesCents: z.array(CentsSchema),
	pricesComplete: z.boolean(),
	qualifyingCount: CountSchema,
	provingOffer: CrawlDataProvingOfferSchema.nullable()
});
const CrawlDataPriceRowSchema = z.object({
	id: z.string(),
	languageId: LanguageIdSchema,
	languageLabel: z.string().meta({ examples: ["English", "German"] }),
	isFoil: z.boolean().nullable(),
	isReverseHolo: z.boolean().nullable(),
	isFirstEd: z.boolean().nullable(),
	isProSeller: z.boolean(),
	prices: ConditionPricesSchema,
	details: z.object({
		NM: CrawlDataConditionDetailSchema,
		EX: CrawlDataConditionDetailSchema,
		GD: CrawlDataConditionDetailSchema,
		LP: CrawlDataConditionDetailSchema,
		PL: CrawlDataConditionDetailSchema,
		PO: CrawlDataConditionDetailSchema
	}),
	observationCount: CountSchema,
	parserVersion: z.number().int().min(0),
	firstSeenAt: IsoTimeSchema,
	lastSeenAt: IsoTimeSchema,
	isLatest: z.boolean()
});
const CrawlDataProductTargetSchema = z.object({
	ruleKey: z.string().nullable(),
	matrixDepth: z.enum(CRAWL_MATRIX_DEPTHS).nullable(),
	classification: z.string().nullable(),
	state: z.string(),
	lastAttemptAt: IsoTimeSchema.nullable(),
	lastSuccessAt: IsoTimeSchema.nullable(),
	lastOutcome: z.string().nullable(),
	consecutiveFailures: CountSchema,
	nextAttemptAt: IsoTimeSchema.nullable(),
	freshUntil: IsoTimeSchema.nullable(),
	lastRequestCount: CountSchema.nullable(),
	lastResult: z.record(z.string(), z.unknown()).nullable()
});
const CrawlDataProductSchema = z.object({
	product: z.object({
		idProduct: IdProductSchema,
		gameId: IdGameSchema,
		gameName: z.string(),
		name: z.string(),
		expansion: z.string().nullable(),
		url: z.string().nullable()
	}),
	target: CrawlDataProductTargetSchema.nullable(),
	rows: z.array(CrawlDataPriceRowSchema)
});
const CrawlQueueDataProductInputSchema = z.object({ idProduct: IdProductSchema });
/** `queued`: sent to the workers now. `already_running`: a crawl is running; it runs again right after. */
const CRAWL_QUEUE_DATA_PRODUCT_OUTCOMES = ["queued", "already_running"];
const CrawlQueueDataProductOutputSchema = z.object({ outcome: z.enum(CRAWL_QUEUE_DATA_PRODUCT_OUTCOMES) });

//#endregion
export { CRAWL_DATA_CONDITIONS, CRAWL_DATA_PRODUCT_STATUSES, CRAWL_QUEUE_DATA_PRODUCT_OUTCOMES, CrawlDataConditionDetailSchema, CrawlDataCoverageInputSchema, CrawlDataCoverageLanguageSchema, CrawlDataCoverageSchema, CrawlDataPriceRowSchema, CrawlDataProductFinishSchema, CrawlDataProductInputSchema, CrawlDataProductMatchSchema, CrawlDataProductMatchesSchema, CrawlDataProductRowSchema, CrawlDataProductSchema, CrawlDataProductTargetSchema, CrawlDataProductsPageSchema, CrawlDataProvingOfferSchema, CrawlListDataProductsInputSchema, CrawlQueueDataProductInputSchema, CrawlQueueDataProductOutputSchema, CrawlSearchDataProductsInputSchema };
//# sourceMappingURL=data.js.map